import React, { useState } from "react"
import { useSelector, useDispatch } from "react-redux"
import { Row, Col } from "react-bootstrap"

import Images from "assets/images"
import { EDIT, IDLE, PENDING, VIEW, WARNING } from "const"
import { SilSafetyFunction } from "server/models"
import {
    StateType,
    hasProjectAccess,
} from "client/utils"
import {
    projectsRoute,
    silSafetyFunctionsRoute,
    silDevicesRoute,
    silSubsystemsRoute,
} from "client/parts/routes"
import {
    PRM_SIL_SAFETY_FUNCTION,
    StoreType,
    LoadSilSafetyFunctionById,
    CalculateSafetyFunction,
    IncreaseSilCalculations,
    addModal,
    setSilResultReloadList,
} from "client/store"
import {
    Collapse,
    Loader,
    PageForm,
    AlertMessage,
    Logo,
    Link,
    Button,
    ConfirmWrapper,
    Confirm,
    ConfirmTitle,
    ConfirmDescription,
    ConfirmBottom,
} from "client/components"
import {
    BlockTitle,
    BlockContent,
    List,
    ListRow,
    CalculateButton,
} from "./styled"

let targetSafetyFunction: SilSafetyFunction = null
let calculationNumber = 0

const Overview = () => {
    const safetyFunctions = useSelector((state: StoreType) => state.silResultSafetyFunction.safetyFunctionList)
    const updatedDevices = useSelector((state: StoreType) => state.silResultSafetyFunction.updatedDevices)
    const project = useSelector((state: StoreType) => state.project.projectView)

    const packageUsage = useSelector((state: StoreType) => state.system.packageUsage)
    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [calculateState, setCalculateState] = useState<StateType>(IDLE)
    const [showConfirm, setShowConfirm] = useState(false)

    const calculated = safetyFunctions.filter((row) => row.calculated)
    const notCalculated = safetyFunctions.filter((row) => !row.calculated)

    const onCalculate = (safetyFunction: SilSafetyFunction) => {
        targetSafetyFunction = safetyFunction
        calculationNumber = 1
        setShowConfirm(true)
    }

    const onCalculateAll = () => {
        calculationNumber = notCalculated.length
        if (calculationNumber === 1) targetSafetyFunction = notCalculated[0]
        setShowConfirm(true)
    }

    const onCancelConfirm = () => {
        targetSafetyFunction = null
        calculationNumber = 0
        setShowConfirm(false)
    }

    const onConfirm = () => {
        setShowConfirm(false)
        setCalculateState(PENDING)
        if (calculationNumber === 1) {
            LoadSilSafetyFunctionById(targetSafetyFunction.id, targetSafetyFunction.projectId)
            .then((safetyFunction) => (
                Promise.all([
                    CalculateSafetyFunction(safetyFunction, safetyFunction.subsystems),
                    IncreaseSilCalculations(userData.accountId, dispatch),
                ]).then(() => {
                    setCalculateState(IDLE)

                    // reload list
                    dispatch(setSilResultReloadList(false))
                    setTimeout(() => {
                        dispatch(setSilResultReloadList(true))
                    }, 0)
                })
            ))
        } else {
            /*
                Calculate all functions one by one.
                If to use "Promise.all" and there are several the same devices in not calculated functions -
                the system will add all of them to the DB
            */
            let indexOneByOne: number

            const calculateOneByOne = () => {
                const notCalculatedRow = notCalculated[indexOneByOne]
                if (notCalculatedRow) {
                    LoadSilSafetyFunctionById(notCalculatedRow.id, notCalculatedRow.projectId)
                    .then((safetyFunction) => {
                        CalculateSafetyFunction(safetyFunction, safetyFunction.subsystems)
                        .then(() => {
                            indexOneByOne += 1
                            calculateOneByOne()
                        })
                    })
                } else {
                    setCalculateState(IDLE)

                    // reload list
                    dispatch(setSilResultReloadList(false))
                    setTimeout(() => {
                        dispatch(setSilResultReloadList(true))
                    }, 0)
                }
            }

            IncreaseSilCalculations(userData.accountId, dispatch, notCalculated.length)
            .then(() => {
                indexOneByOne = 0
                calculateOneByOne()
            })
        }
    }

    const devicesUpdated = lang.devicesUpdated.split("{0}")
    const updateDevicesUrl = `${projectsRoute}/${VIEW}/${project.id}${silDevicesRoute}`
    const updateSubsystemsUrl = `${projectsRoute}/${VIEW}/${project.id}${silSubsystemsRoute}`

    const editSafetyFunctionAccess = hasProjectAccess(PRM_SIL_SAFETY_FUNCTION, EDIT)
    const baseUrl = `${projectsRoute}/${VIEW}/${project.id}${silSafetyFunctionsRoute}/${editSafetyFunctionAccess ? EDIT : VIEW}`

    const { maxCalculations, usedCalculations } = packageUsage

    let leftCalculations = ""
    if (maxCalculations === null) leftCalculations = lang.unlimitedCalculations
    else leftCalculations = lang.leftCalculations.replace("{0}", usedCalculations.toString()).replace("{1}", maxCalculations.toString())

    return (
        <Collapse show title={lang.overview}>
            {
                calculateState === PENDING
                && <Loader />
            }

            <PageForm>
                {
                    updatedDevices > 0 && (
                        <AlertMessage variant={WARNING}>
                            <b>{lang.warning}</b>
                            <div>
                                {devicesUpdated[0]}
                                <Link
                                    href={updateDevicesUrl}
                                    onClick={() => dispatch(addModal({ url: updateDevicesUrl }))}
                                >
                                    {lang.here}
                                </Link>
                                {devicesUpdated[1]}
                                <Link
                                    href={updateSubsystemsUrl}
                                    onClick={() => dispatch(addModal({ url: updateSubsystemsUrl }))}
                                >
                                    {lang.here}
                                </Link>
                                {devicesUpdated[2]}
                            </div>
                        </AlertMessage>
                    )
                }

                <Logo
                    logo={userData.accountLogo || Images.accountLogo}
                    width="30%"
                    offsetBottom
                />
                <Row>
                    <Col xs={12}>
                        <BlockTitle>{project.name}</BlockTitle>

                        <BlockTitle>{lang.projectPublicId}</BlockTitle>
                        <BlockContent>{project.publicId}</BlockContent>

                        <BlockTitle>{lang.projectDescription}</BlockTitle>
                        <BlockContent dangerouslySetInnerHTML={{ __html: project.description }} />
                    </Col>

                    {
                        calculated.length > 0 && (
                            <Col xs={12}>
                                <BlockTitle>{lang.calculatedSafetyFunctions}</BlockTitle>
                                <List>
                                    {
                                        calculated.map((safetyFunction) => {
                                            const url = `${baseUrl}/${safetyFunction.id}`
                                            return (
                                                <ListRow key={safetyFunction.id}>
                                                    <Link
                                                        href={url}
                                                        onClick={() => dispatch(addModal({
                                                            url,
                                                            onClose: () => {
                                                                // reload result list
                                                                dispatch(setSilResultReloadList(false))
                                                                setTimeout(() => {
                                                                    dispatch(setSilResultReloadList(true))
                                                                }, 0)
                                                            },
                                                        }))}
                                                    >
                                                        {safetyFunction.name}
                                                    </Link>
                                                </ListRow>
                                            )
                                        })
                                    }
                                </List>
                            </Col>
                        )
                    }
                    {
                        notCalculated.length > 0 && (
                            <Col xs={12}>
                                <BlockTitle>{lang.toBeCalculatedSafetyFunctions}</BlockTitle>
                                <List>
                                    {
                                        notCalculated.map((safetyFunction) => {
                                            const url = `${baseUrl}/${safetyFunction.id}`
                                            return (
                                                <ListRow key={safetyFunction.id}>
                                                    <Link
                                                        href={url}
                                                        onClick={() => dispatch(addModal({
                                                            url,
                                                            onClose: () => {
                                                                // reload result list
                                                                dispatch(setSilResultReloadList(false))
                                                                setTimeout(() => {
                                                                    dispatch(setSilResultReloadList(true))
                                                                }, 0)
                                                            },
                                                        }))}
                                                    >
                                                        {safetyFunction.name}
                                                    </Link>
                                                    <CalculateButton
                                                        disabled={!editSafetyFunctionAccess}
                                                        onClick={() => onCalculate(safetyFunction)}
                                                    >
                                                        {lang.calculate}
                                                    </CalculateButton>
                                                </ListRow>
                                            )
                                        })
                                    }

                                    <CalculateButton
                                        disabled={!editSafetyFunctionAccess}
                                        onClick={onCalculateAll}
                                    >
                                        {lang.calculateAll}
                                    </CalculateButton>
                                </List>
                            </Col>
                        )
                    }
                    {
                        safetyFunctions.length === 0 && (
                            <Col xs={12}>
                                <BlockTitle>{lang.noSafetyFunctions}</BlockTitle>
                            </Col>
                        )
                    }
                </Row>
            </PageForm>

            <ConfirmWrapper show={showConfirm} onClose={onCancelConfirm}>
                <Confirm align="center">
                    <ConfirmTitle>{lang.confirmCalculation}</ConfirmTitle>
                    <ConfirmDescription align="start">{leftCalculations}</ConfirmDescription>
                    {
                        calculationNumber > 1 && (
                            <ConfirmDescription align="start">
                                {lang.calculationCost.replace("{0}", calculationNumber.toString())}
                            </ConfirmDescription>
                        )
                    }

                    <ConfirmBottom>
                        <Button row onClick={onCancelConfirm}>{lang.cancel}</Button>
                        <Button
                            variant="success"
                            row
                            disabled={maxCalculations !== null && (
                                calculationNumber > 1
                                    ? usedCalculations + calculationNumber > maxCalculations
                                    : usedCalculations >= maxCalculations
                            )}
                            onClick={onConfirm}
                        >
                            {lang.confirm}
                        </Button>
                    </ConfirmBottom>
                </Confirm>
            </ConfirmWrapper>
        </Collapse>
    )
}
export default Overview
